import { View, Text, StyleSheet, ActivityIndicator, Platform, TouchableOpacity, ScrollView } from 'react-native';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import * as Sentry from '@sentry/react-native';
import { supabase, isShortId, toError } from '../../lib/supabase';
import { resolveProjectPaperByShortId } from '../../lib/shortIdResolver';
import PaperDetailView from './PaperDetailView';
import SharePaperModal from './SharePaperModal';
import {
    PaperData,
    ProjectPaperContext,
    DigestEntryContext,
    PaperPageState,
} from './types';
import MetaTags from '../MetaTags';
import Header from '../Header';
import Footer from '../Footer';
import { getDigestUrl, generateDigestSlug, getShortId, createSlug } from '../../lib/paperUrl';
import PrivateDigestAccessCard from '../digest/PrivateDigestAccessCard';
import colors from '../../lib/colors';
import { useAuth } from '../../lib/AuthContext';
import { useSession } from '../../lib/SessionContext';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold } from '../../lib/fonts';
import { usePaperSummary } from '../../hooks/usePaperSummary';
import { useProjectPaperFeedbackBindings } from '../../hooks/useProjectPaperFeedbackBindings';
import { canAccessMainApp } from '../../utils/authHelpers';
import {
    canGenerateUnscopedPaperSummary,
    isSummaryEligibleProjectPaper,
    resolvePaperSummaryProjectId,
} from '../../lib/paperSummaryAccess';
import { PRIVATE_OR_NOT_FOUND, isPrivateOrNotFoundError, toPrivateOrNotFoundError } from '../../lib/accessErrors';
import { ProjectPaperFeedback } from '../../lib/projectPaperFeedback';
import { normalizePaperData } from '../../lib/paperAuthors';
import { InvalidDigestPaperResponseError, validateDigestPaperResponse } from '../../lib/digestPaperResponse';
import {
    fetchCurrentEntitlements,
    invalidateEntitlementSnapshotCache,
} from '../../lib/entitlements';
import { classifyBillingPreviewDigestAccess } from '../../lib/billingPreviewState';
import { fetchPublicDigestPreviewAccess } from '../../lib/publicDigestPreviewAccess';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { type LocalizedMessage, useOptionalResolvedMessage } from '../../lib/i18n/messages';
import { localizedPublicUrl, stripStaticPublicExportPrefix } from '../../lib/i18n/publicWeb';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

interface DigestPaperData {
    rank_position: number;
    project_paper: {
        id: string;
        short_id: string | null;
        slug: string | null;
        paper_title: string | null;
        summary: string | null;
        relevance_score: number | null;
        relevance_class: number | null;
        processing_state: string | null;
        matching_details: any;
        personalized_note: string | null;
        project_paper_feedback: ProjectPaperFeedback | null;
        paper: PaperData;
    };
}

interface DigestIssueData {
    id: string;
    short_id: string | null;
    project: {
        id: string;
        name: string;
        visibility: string;
        user_id: string | null;
    };
}

interface DigestPaperPageContentProps {
    issueId: string;
    paperId: string;
    paperSlug?: string | null;
}

function normalizePathname(pathname: string): string {
    const normalized = pathname
        .replace(/\/+/g, '/')
        .replace(/\/+$/, '');
    return normalized || '/';
}

/**
 * Shared component for digest paper pages
 * Used by both /digest/[issueId]/p/[paperId] and /digest/[issueId]/[...slug]/p/[paperId]
 * Shows paper details plus digest-specific context (rank, summary, scores)
 *
 * Note: paperId is actually project_paper_id, not paper.id
 */
export default function DigestPaperPageContent({ issueId, paperId, paperSlug }: DigestPaperPageContentProps) {
    const { messageLocale } = useAppLocale();
    const router = useRouter();
    const { openAuth } = useAuth();
    const { session, isLoading: isSessionLoading } = useSession();
    const { fontsLoaded } = useFontLoading();
    const [digestIssue, setDigestIssue] = useState<DigestIssueData | null>(null);
    const [paperData, setPaperData] = useState<DigestPaperData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const resolvedError = useOptionalResolvedMessage(error);
    const [isOwner, setIsOwner] = useState(false);
    const [isPublic, setIsPublic] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const urlPaperSlug = paperSlug || null;
    const lastCanonicalRedirect = useRef<string | null>(null);

    // Extract paper ID and project ID for hooks (may be null during loading)
    const currentPaperId = paperData?.project_paper?.paper?.id || null;
    const currentProjectId = digestIssue?.project?.id || null;
    const currentProjectPaperId = paperData?.project_paper?.id || null;
    const canGenerateUnscopedSummary = canGenerateUnscopedPaperSummary(session?.user?.id);

    // Use paper summary hook for AI summary feature
    // Must be called at the top level, before any conditional returns
    const {
        summary,
        job: summaryJob,
        status: summaryStatus,
        error: summaryError,
        summaryLimitReached,
        generateSummary,
        retry: retrySummary,
    } = usePaperSummary(
        currentPaperId,
        resolvePaperSummaryProjectId({
            canGenerateUnscoped: canGenerateUnscopedSummary,
            viewerOwnsProject: isOwner,
            projectId: currentProjectId,
        })
    );

    const {
        onSetFeedback,
        onClearFeedback,
        feedbackPending,
    } = useProjectPaperFeedbackBindings({
        projectId: currentProjectId || undefined,
        projectPaperId: currentProjectPaperId || undefined,
        feedback: paperData?.project_paper?.project_paper_feedback || null,
        surface: 'digest_paper',
        onOptimisticChange: (nextFeedback) => {
            setPaperData((current) => (
                current ? {
                    ...current,
                    project_paper: {
                        ...current.project_paper,
                        project_paper_feedback: nextFeedback,
                    },
                } : current
            ));
        },
    });

    // Get current URL for redirect after login
    const getCurrentUrl = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            return window.location.pathname;
        }
        return `/digest/${issueId}/p/${paperId}`;
    };

    // Check ownership and visibility when digest loads
    useEffect(() => {
        async function checkOwnershipAndVisibility() {
            if (!digestIssue) return;

            const { data: { session } } = await supabase.auth.getSession();
            setIsOwner(digestIssue.project.user_id === session?.user?.id);
            setIsPublic(digestIssue.project.visibility === 'public');
        }
        checkOwnershipAndVisibility();
    }, [digestIssue]);

    // Redirect to canonical URL if current URL doesn't match
    useEffect(() => {
        // Only canonicalize legacy routes without a paper slug.
        // The slug route is already canonical and should not navigate on mount.
        if (!digestIssue || !paperData || Platform.OS !== 'web' || urlPaperSlug) return;

        // Build canonical URL components
        const digestShortId = digestIssue.short_id || getShortId(digestIssue.id);
        const digestSlug = generateDigestSlug(digestIssue.project.name);
        const projectPaper = paperData.project_paper;
        const paperShortId = projectPaper.short_id || getShortId(projectPaper.id);
        const canonicalPaperSlug = projectPaper.slug
            || (projectPaper.paper_title ? createSlug(projectPaper.paper_title) : '')
            || (projectPaper.paper?.title ? createSlug(projectPaper.paper.title) : 'paper');

        const canonicalPath = `/digest/${digestShortId}/${digestSlug}/p/${paperShortId}/${canonicalPaperSlug}`;
        const canonicalTarget = canonicalPath;

        // Check if current path matches canonical
        if (typeof window !== 'undefined') {
            const currentPath = stripStaticPublicExportPrefix(window.location.pathname);
            const normalizedCurrentPath = normalizePathname(currentPath);
            const normalizedCanonicalPath = normalizePathname(canonicalPath);

            if (normalizedCurrentPath === normalizedCanonicalPath) {
                lastCanonicalRedirect.current = null;
                return;
            }

            if (lastCanonicalRedirect.current === canonicalTarget) {
                return;
            }

            lastCanonicalRedirect.current = canonicalTarget;
            console.log('📄 [DigestPaperPage] Redirecting to canonical URL:', canonicalTarget);
            router.replace(canonicalTarget);
        }
    }, [digestIssue, paperData, router, urlPaperSlug]);

    // Handle share button press
    const handleSharePress = () => {
        setShowShareModal(true);
    };

    // Handle share modal close
    const handleCloseShareModal = () => {
        setShowShareModal(false);
    };

    // Handle visibility change from modal
    const handleVisibilityChanged = () => {
        setIsPublic(true);
    };

    useEffect(() => {
        if (issueId && paperId) {
            fetchDigestPaper();
        }
    }, [issueId, paperId, urlPaperSlug]);

    const fetchDigestPaper = async () => {
        setLoading(true);
        setError(null);

        try {
            console.log('📄 [DigestPaperPage] Fetching paper:', { issueId, paperId });

            // Determine if we're querying by short_id or full UUID
            const idField = isShortId(issueId) ? 'short_id' : 'id';

            // Step 1: Fetch digest issue to check permissions
            const { data: digestRows, error: digestError } = await supabase
                .from('email_digest')
                .select(`
                    id,
                    short_id,
                    project:project_id (
                        id,
                        name,
                        visibility,
                        user_id
                    )
                `)
                .eq(idField, issueId)
                .limit(1);

            if (digestError) {
                const accessError = toPrivateOrNotFoundError(digestError);
                if (accessError) {
                    setError(PRIVATE_OR_NOT_FOUND);
                    setLoading(false);
                    return;
                }

                console.error('📄 [DigestPaperPage] Error fetching digest:', digestError);
                Sentry.captureException(toError(digestError), {
                    extra: { issueId, paperId }
                });
                throw toError(digestError);
            }

            const digestData = digestRows?.[0] || null;

            if (!digestData) {
                Sentry.captureMessage('Digest data null in DigestPaperPageContent', {
                    level: 'info',
                    extra: { issueId, paperId }
                });
                setError(PRIVATE_OR_NOT_FOUND);
                setLoading(false);
                return;
            }

            // Step 2: Check access permissions
            const { data: { session } } = await supabase.auth.getSession();

            // If project is null, treat as not found
            if (!digestData.project) {
                setError(PRIVATE_OR_NOT_FOUND);
                setLoading(false);
                return;
            }

            const isOwner = digestData.project.user_id === session?.user?.id;
            const isPublic = digestData.project.visibility === 'public';

            console.log('📄 [DigestPaperPage] Access check:', {
                isOwner,
                isPublic,
                projectUserId: digestData.project.user_id,
                sessionUserId: session?.user?.id
            });

            if (!isOwner && !isPublic) {
                setError(PRIVATE_OR_NOT_FOUND);
                setLoading(false);
                return;
            }

            let restrictToPreviewPapers = false;
            let publicPreviewVisibleIds: string[] | null = null;
            if (isPublic) {
                const publicAccess = await fetchPublicDigestPreviewAccess(digestData.id);
                if (publicAccess.project_id !== digestData.project.id) {
                    throw new Error('Paperzilla returned mismatched public preview access.');
                }
                restrictToPreviewPapers = publicAccess.preview_access === 'restricted';
                publicPreviewVisibleIds = publicAccess.visible_project_paper_ids;
            } else if (isOwner) {
                if (!session?.access_token || !session.user?.id) {
                    throw new Error('Could not verify preview access. Please reload and try again.');
                }
                invalidateEntitlementSnapshotCache(session.user.id);
                const snapshot = await fetchCurrentEntitlements(session.access_token, {
                    forceRefresh: true,
                    userId: session.user.id,
                });
                const classification = classifyBillingPreviewDigestAccess(
                    snapshot,
                    digestData.id
                );
                if (classification === 'restricted') {
                    restrictToPreviewPapers = true;
                } else if (classification !== 'unrestricted') {
                    throw new Error('Could not verify preview access. Please reload and try again.');
                }
            }

            // Step 3: Fetch paper with digest context
            // First, resolve paperId to full UUID if it's a short_id
            let projectPaperFullId = paperId;
            if (isShortId(paperId)) {
                const result = await resolveProjectPaperByShortId(paperId, digestData.id, urlPaperSlug);
                if (result.error) {
                    console.error('📄 [DigestPaperPage] Error resolving project_paper short_id:', result.error);
                    Sentry.captureException(toError(result.error), {
                        extra: { issueId, paperId, digestId: digestData.id }
                    });
                    throw toError(result.error);
                }

                if (!result.data) {
                    console.error('📄 [DigestPaperPage] Could not find paper with short_id in this digest:', paperId);
                    Sentry.captureMessage('Paper not found in digest', {
                        level: 'info',
                        extra: { issueId, paperId, digestId: digestData.id }
                    });
                    setError(msg({ id: 'digestPaper.notFound', message: 'Paper not found in this digest' }));
                    setLoading(false);
                    return;
                }

                projectPaperFullId = result.data.project_paper_id;
            } else {
                // Full UUID: verify it's a project_paper id in this digest; else treat as paper.id
                const { data: directMatch, error: directError } = await supabase
                    .from('digest_papers')
                    .select('project_paper_id')
                    .eq('digest_id', digestData.id)
                    .eq('project_paper_id', paperId)
                    .maybeSingle();

                if (directError) {
                    console.error('📄 [DigestPaperPage] Error validating project_paper_id:', directError);
                    Sentry.captureException(toError(directError), {
                        extra: { issueId, paperId, digestId: digestData.id }
                    });
                    throw toError(directError);
                }

                if (!directMatch) {
                    const { data: paperMatches, error: paperError } = await supabase
                        .from('project_paper')
                        .select('id, identified_at, digest_papers!inner(digest_id)')
                        .eq('digest_papers.digest_id', digestData.id)
                        .eq('paper_id', paperId)
                        .order('identified_at', { ascending: true });

                    if (paperError) {
                        console.error('📄 [DigestPaperPage] Error looking up by paper_id:', paperError);
                        Sentry.captureException(toError(paperError), {
                            extra: { issueId, paperId, digestId: digestData.id }
                        });
                        throw toError(paperError);
                    }

                    if (paperMatches && paperMatches.length > 0) {
                        if (paperMatches.length > 1) {
                            Sentry.captureMessage('Multiple project_papers for paper in digest', {
                                level: 'warning',
                                extra: { issueId, paperId, digestId: digestData.id, matchCount: paperMatches.length }
                            });
                        }
                        projectPaperFullId = paperMatches[0].id;
                    } else {
                        console.error('📄 [DigestPaperPage] Could not find paper in digest:', paperId);
                        Sentry.captureMessage('Paper not found in digest (paper_id lookup)', {
                            level: 'info',
                            extra: { issueId, paperId, digestId: digestData.id }
                        });
                        setError(msg({ id: 'digestPaper.notFound', message: 'Paper not found in this digest' }));
                        setLoading(false);
                        return;
                    }
                }
            }

            if (
                restrictToPreviewPapers
                && publicPreviewVisibleIds
                && !publicPreviewVisibleIds.includes(projectPaperFullId)
            ) {
                setError(msg({ id: 'digestPaper.notFound', message: 'Paper not found in this digest' }));
                setLoading(false);
                return;
            }

            let digestPaperQuery = supabase
                .from('digest_papers')
                .select(`
                    rank_position,
                    project_paper:project_paper_id (
                        id,
                        short_id,
                        slug,
                        paper_title,
                        summary,
                        relevance_score,
                        relevance_class,
                        processing_state,
                        matching_details,
                        personalized_note,
                        project_paper_feedback (
                            vote,
                            downvote_reason,
                            updated_at
                        ),
                        paper:paper_id (
                            id,
                            short_id,
                            title,
                            abstract,
                            authors,
                            published_date,
                            pdf_url,
                            pdf_availability_status,
                            pdf_availability_source,
                            pdf_availability_license,
                            pdf_availability_checked_at,
                            pdf_availability_next_check_at,
                            url,
                            doi,
                            source_id,
                            source_paper_id,
                            source_paper_version
                        )
                    )
                `)
                .eq('digest_id', digestData.id)
                .eq('project_paper_id', projectPaperFullId)
                .limit(2);
            if (restrictToPreviewPapers && publicPreviewVisibleIds) {
                digestPaperQuery = publicPreviewVisibleIds.length > 0
                    ? digestPaperQuery.in('project_paper_id', publicPreviewVisibleIds)
                    : digestPaperQuery.limit(0);
            } else if (restrictToPreviewPapers) {
                digestPaperQuery = digestPaperQuery.lte('rank_position', 3);
            }
            // Normalize a JSON list locally instead of relying on object content negotiation.
            const { data: digestPaper, error: paperError } = await digestPaperQuery.maybeSingle();

            if (paperError) {
                console.error('📄 [DigestPaperPage] Error fetching paper:', paperError);
                // maybeSingle handles zero rows; remaining cardinality errors are not missing papers.
                if (paperError.code === 'PGRST116') {
                    throw new Error('Digest paper query did not return a unique row');
                }
                throw toError(paperError);
            }

            if (digestPaper == null) {
                Sentry.captureMessage('Digest paper null', {
                    level: 'info',
                    extra: { issueId, paperId, digestId: digestData.id, projectPaperFullId }
                });
                setError(msg({ id: 'digestPaper.notFound', message: 'Paper not found in this digest' }));
                setLoading(false);
                return;
            }

            validateDigestPaperResponse(digestPaper);

            console.log('📄 [DigestPaperPage] Successfully fetched paper with context');

            setDigestIssue(digestData as DigestIssueData);
            setPaperData({
                ...digestPaper,
                project_paper: {
                    ...digestPaper.project_paper,
                    paper: normalizePaperData(digestPaper.project_paper.paper),
                },
            } as unknown as DigestPaperData);
            setError(null);
        } catch (err: any) {
            console.error('📄 [DigestPaperPage] Unexpected error:', err);
            Sentry.captureException(err, {
                extra: {
                    issueId,
                    paperId,
                    ...(err instanceof InvalidDigestPaperResponseError
                        ? { responseShape: err.responseShape }
                        : {}),
                }
            });
            setError(isPrivateOrNotFoundError(err)
                ? PRIVATE_OR_NOT_FOUND
                : msg({
                    id: 'paper.loadFailed',
                    message: 'Failed to load paper',
                    comment: 'Generic paper loading error; raw errors remain in logs and Sentry.',
                }));
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <View style={styles.container}>
                <Header />
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                    <Text style={styles.loadingText}><Trans id="papers.digestpaperpagecontent.loadingPaper" comment="UI copy in components/papers/DigestPaperPageContent.tsx">Loading paper...</Trans></Text>
                </View>
                <Footer />
            </View>
        );
    }

    // Error state
    if (error || !paperData || !digestIssue) {
        const isPrivateOrNotFound = isPrivateOrNotFoundError(error);

        const handleSignIn = () => {
            openAuth({ redirectTo: getCurrentUrl() });
        };

        const handleGoHome = () => {
            router.push('/');
        };

        // Non-private errors (generic error display)
        if (!isPrivateOrNotFound) {
            return (
                <View style={styles.container}>
                    <Header />
                    <View style={styles.errorContainer}>
                        <View style={[styles.errorIconContainer, styles.errorIconRed]}>
                            <FontAwesome6
                                name="circle-exclamation"
                                size={28}
                                color={colors.errorRed}
                            />
                        </View>
                        <Text style={[styles.errorTitle, fontsLoaded && interSemiBold]}><Trans id="papers.digestpaperpagecontent.errorLoadingPaper" comment="UI copy in components/papers/DigestPaperPageContent.tsx">
                            Error Loading Paper
                        </Trans></Text>
                        <Text style={styles.errorText}>
                            {resolvedError || <Trans id="papers.digestpaperpagecontent.thePaperYouAreLookingForCouldNotBeFound" comment="Conditional UI copy in components/papers/DigestPaperPageContent.tsx">The paper you are looking for could not be found.</Trans>}
                        </Text>
                        <Text style={styles.supportText}><Trans id="papers.digestpaperpagecontent.needHelpContactUsAtHelloPaperzillaAi" comment="UI copy in components/papers/DigestPaperPageContent.tsx">
                            Need help? Contact us at hello@paperzilla.ai
                        </Trans></Text>
                    </View>
                    <Footer />
                </View>
            );
        }

        // Private digest - different UI based on auth state
        return (
            <View style={styles.container}>
                <Header />
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.privateScrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.privateContentWrapper}>
                        <PrivateDigestAccessCard
                            isAuthenticated={!isSessionLoading && canAccessMainApp(session)}
                            onSignIn={handleSignIn}
                            onSwitchAccount={() => openAuth({ redirectTo: getCurrentUrl(), forSwitchAccount: true })}
                            fontsLoaded={fontsLoaded}
                        />

                        {/* Info card about Paperzilla */}
                        <View style={styles.infoCard}>
                            <View style={styles.infoHeader}>
                                <FontAwesome6 name="circle-info" size={16} color={colors.primaryGreen} />
                                <Text style={[styles.infoTitle, fontsLoaded && interSemiBold]}><Trans id="papers.digestpaperpagecontent.whatIsPaperzilla" comment="UI copy in components/papers/DigestPaperPageContent.tsx">
                                    What is Paperzilla?
                                </Trans></Text>
                            </View>
                            <Text style={styles.infoText}><Trans id="papers.digestpaperpagecontent.paperzillaWatchesNewPreprintsAndPubmedIndexedRecordsRanksWhatMatches" comment="UI copy in components/papers/DigestPaperPageContent.tsx">
                                Paperzilla watches new preprints and PubMed-indexed records, ranks what matches your interests, and delivers high-signal feeds you can skim in minutes — in-app, via RSS/Atom, email digest, or programmatically.
                            </Trans></Text>
                            <TouchableOpacity
                                style={styles.learnMoreButton}
                                onPress={handleGoHome}
                                activeOpacity={0.8}
                            >
                                <Text style={[styles.learnMoreText, fontsLoaded && interSemiBold]}><Trans id="papers.digestpaperpagecontent.learnMore" comment="UI copy in components/papers/DigestPaperPageContent.tsx">
                                    Learn more
                                </Trans></Text>
                                <FontAwesome6 name="arrow-right" size={12} color={colors.primaryGreen} />
                            </TouchableOpacity>
                        </View>

                        {/* Support text */}
                        <Text style={styles.supportText}><Trans id="papers.digestpaperpagecontent.needHelpContactUsAtHelloPaperzillaAi" comment="UI copy in components/papers/DigestPaperPageContent.tsx">
                            Need help? Contact us at hello@paperzilla.ai
                        </Trans></Text>
                    </View>
                    <Footer />
                </ScrollView>
            </View>
        );
    }

    // Success state - render paper with project and digest entry context
    const paper = paperData.project_paper.paper;
    const projectPaperContext: ProjectPaperContext = {
        projectId: digestIssue.project.id,
        projectName: digestIssue.project.name,
        projectOwnerId: digestIssue.project.user_id,
        projectPaperId: paperData.project_paper.id,
        projectPaperShortId: paperData.project_paper.short_id,
        feedback: paperData.project_paper.project_paper_feedback,
        recommendationSummary: paperData.project_paper.summary,
        relevanceScore: paperData.project_paper.relevance_score,
        relevanceClass: paperData.project_paper.relevance_class,
        processingState: paperData.project_paper.processing_state,
        matchingDetails: paperData.project_paper.matching_details,
        personalizedNote: paperData.project_paper.personalized_note,
    };
    const digestEntryContext: DigestEntryContext = {
        digestId: digestIssue.id,
        digestName: digestIssue.project.name,
        rankPosition: paperData.rank_position,
    };

    // Build canonical URL components
    const digestShortId = digestIssue.short_id || getShortId(digestIssue.id);
    const digestSlug = generateDigestSlug(digestIssue.project.name);
    const projectPaper = paperData.project_paper;
    const paperShortId = projectPaper.short_id || getShortId(projectPaper.id);
    const canonicalPaperSlug = projectPaper.slug
        || (projectPaper.paper_title ? createSlug(projectPaper.paper_title) : '')
        || (paper.title ? createSlug(paper.title) : 'paper');

    // Generate canonical share URL
    const canonicalPath = `/digest/${digestShortId}/${digestSlug}/p/${paperShortId}/${canonicalPaperSlug}`;
    const shareUrl = localizedPublicUrl(canonicalPath, messageLocale, {
        allowTestLocale: messageLocale === 'en-XA',
    });

    // Show share button for owners or when digest is public
    const canShare = isOwner || isPublic;
    const viewer = isOwner
        ? 'owner'
        : canAccessMainApp(session)
            ? 'non_owner'
            : 'anonymous';
    const canGenerateSummary = (
        isOwner && isSummaryEligibleProjectPaper({
            relevanceClass: paperData.project_paper.relevance_class,
            processingState: paperData.project_paper.processing_state,
        })
    ) || canGenerateUnscopedSummary;
    const pageState: PaperPageState = {
        surface: 'digest',
        viewer,
        access: 'accessible',
        canShare,
        canFeedback: isOwner,
        hasRecommendationContext: true,
        summaryState: summaryStatus,
        primaryCta: canGenerateSummary && summaryStatus === 'not_requested'
            ? 'generate_summary'
            : canGenerateSummary && summaryStatus === 'failed'
                ? 'retry_summary'
                : !isOwner && isPublic && (summaryStatus === 'not_requested' || summaryStatus === 'failed')
                    ? viewer === 'anonymous'
                        ? 'sign_up'
                        : 'create_digest'
                    : 'none',
        templateProjectId: !isOwner && isPublic ? digestIssue.project.id : null,
    };

    return (
        <>
            {/* SEO Meta Tags */}
            <MetaTags
                surfaceId="digest-paper"
                canonicalPathname={canonicalPath}
                title={{
                    ...msg({
                        id: 'seo.digestPaper.title',
                        message: '{paperTitle} - {projectName} - Paperzilla',
                        comment: 'Public digest-paper metadata title. Paper and project names are external content and must remain unchanged.',
                    }),
                    values: {
                        paperTitle: paper.title,
                        projectName: digestIssue.project.name,
                    },
                }}
                description={paper.abstract?.substring(0, 160) || msg({
                    id: 'seo.paper.sourceFallbackDescription',
                    message: 'Research paper from arXiv, medRxiv, bioRxiv, ChemRxiv, ChinaXiv, IACR ePrint, or PubMed',
                    comment: 'Fallback metadata description when a public paper has no abstract. Preserve source names.',
                })}
                type="article"
            />

            {/* Paper Detail View with Digest Context */}
            <PaperDetailView
                paper={paper}
                pageState={pageState}
                projectPaperContext={projectPaperContext}
                digestEntryContext={digestEntryContext}
                showBackLink={true}
                backLinkHref={getDigestUrl(digestIssue)}
                backLinkText={`← Back to ${digestIssue.project.name}`}
                summary={summary}
                summaryJob={summaryJob}
                summaryStatus={summaryStatus}
                summaryError={summaryError}
                summaryLimitReached={summaryLimitReached}
                onGenerateSummary={generateSummary}
                onRetry={retrySummary}
                onSharePress={canShare ? handleSharePress : undefined}
                feedbackPending={feedbackPending}
                onSetProjectFeedback={isOwner ? onSetFeedback : undefined}
                onClearProjectFeedback={isOwner ? onClearFeedback : undefined}
            />

            {/* Share Modal */}
            <SharePaperModal
                visible={showShareModal}
                onClose={handleCloseShareModal}
                paperTitle={paper.title}
                shareUrl={shareUrl}
                projectId={digestIssue.project.id}
                isOwner={isOwner}
                isPublic={isPublic}
                onVisibilityChanged={handleVisibilityChanged}
            />
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
        ...(Platform.OS === 'web' && {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
            marginHorizontal: 'auto',
        }),
    },
    loadingText: {
        marginTop: 16,
        fontSize: 16,
        color: colors.text700,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 40,
        paddingVertical: 60,
        ...(Platform.OS === 'web' && {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
            marginHorizontal: 'auto',
        }),
    },
    errorTitle: {
        fontSize: 24,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 12,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 16,
        color: colors.text700,
        textAlign: 'center',
        lineHeight: 24,
    },
    supportText: {
        fontSize: 14,
        color: colors.text500,
        textAlign: 'center',
        marginTop: 24,
        fontStyle: 'italic',
    },
    errorIconContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: '#fef3c7',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
    },
    errorIconRed: {
        backgroundColor: '#fee2e2',
    },
    // Private digest page styles
    scrollView: {
        flex: 1,
    },
    privateScrollContent: {
        flexGrow: 1,
    },
    privateContentWrapper: {
        flex: 1,
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 40,
        maxWidth: 480,
        alignSelf: 'center',
        width: '100%',
    },
    infoCard: {
        backgroundColor: colors.primary100,
        borderRadius: 12,
        padding: 20,
        width: '100%',
        marginTop: 20,
    },
    infoHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },
    infoTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.primaryGreenDark,
    },
    infoText: {
        fontSize: 14,
        color: colors.text700,
        lineHeight: 21,
    },
    learnMoreButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 12,
    },
    learnMoreText: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.primaryGreen,
    },
});
